import { DictType, FileType, ProjectFile } from "@pma/types"
import { Fragment, SyntheticEvent, useEffect, useRef, useState, useMemo, useCallback } from "react"
import {
  ChevronLeft,
  ClipboardList,
  File,
  FileImageIcon,
  FolderIcon,
  FolderOpenIcon,
  MenuIcon,
  X,
  MoreHorizontal,
  GripVertical,
  FolderSearch,
  Download,
  FileAudio2,
  FileSpreadsheet,
  FilePieChart,
  FileText,
  FileJson,
  ArrowUpDown,
  ClipboardX,
  FileCog,
  List,
  Grid,
  EllipsisVertical,
  FileVideo,
  Mail,
  MailQuestion,
  FolderArchive,
} from "lucide-react"
import { Card, CardContent } from "@components/common/card"
import axios from "axios"
import {
  FOLDER_ENDPOINT,
  SERVER_URL,
  MOVE_FILE_ENDPOINT,
  DIGITAL_OCEAN_FILES_ENDPOINT,
  ADD_FILES_TO_PROJECT_ENDPOINT,
  ALL_ORG_USERS_ENDPOINT,
} from "@pma/utils/api-routes"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import { useQuery, useMutation } from "@tanstack/react-query"
import DownloadFile from "./download-file"
import { FilePreview } from "@components/dashboard/preview-file"
import { useNavigate, useSearchParams } from "react-router-dom"
import useTranslator from "@hooks/use-translator"
import PmsLoader from "@components/common/loader"
import { Button } from "@components/common/button"
import EmailFolderFiles from "./email-folder-files"
import AddSubFolder from "./add-sub-folder"
import UploadFile from "./upload-file"
import { Separator } from "@components/common/separator"
import DeleteFolder from "./delete-folder"
import DeleteFile from "@components/dashboard/delete-file"
import MoveFile from "./move-file"
import { cn, convertFileSize } from "@pma/utils/helpers"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@components/common/hover-card"
import ShareFileOnWhatsApp from "./share-file-on-whatsapp"
import PrintFile from "./print-file"
import FilesBatchActions from "./files-batch-actions"
import { Input } from "../common/input"
import FileFilter from "./file-filter"
import { Badge } from "../common/badge"
import InfiniteScroll from "react-infinite-scroll-component"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDraggable,
  useDroppable,
  pointerWithin,
} from "@dnd-kit/core"
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { createPortal } from "react-dom"
import { toast } from "@hooks/use-toast"
import CutFile from "./cut-file"
import CopyFile from "./copy-file"
import { RadioGroup, RadioGroupItem } from "../common/radio-group"
import { Label } from "../common/label"
import ShareFilesFoldersViaEmail from "./share-files-folders-via-email"
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { Switch } from "../common/switch"
import RestoreFolder from "./restore-folder"
import { useAuth } from "@pma/stores/auth-store"
import RestoreFile from "./restore-file"

interface Props {
  project: any
  closeModal?: () => void
  per_page?: number | null
  showAsModal?: boolean
  refetchAllFolder?: () => void
}
export default function FilesView({
  project,
  closeModal,
  per_page = null,
  showAsModal = false,
  refetchAllFolder = () => { },
}: Props) {
  const [activeFileTree, setActiveFileTree] = useState<DictType | null>(null)
  const [activeFileInfinite, setActiveFileInfinite] = useState<DictType | null>(null)
  const [openMobile, setOpenMobile] = useState(false)
  const { authConfig } = useAuth()
  const rootFolder = {
    ...project?.root_folder,
    deleted: project.deleted,
    deleted_at: project.deleted_at,
    deleted_by: project.deleted_by,
  }
  const [breadcrumbs, setBreadcrumbs] = useState<{ [id: number]: any[] }>({
    [rootFolder?.id]: [rootFolder],
  })
  const breadcrumsOverflowLimit = 4
  const [activeFolder, setActiveFolder] = useState<DictType>(rootFolder ?? {})
  const [foldersMeta, setFoldersMeta] = useState<{ [id: number]: number }>({})
  const [folderItems, setFolderItems] = useState<{ [id: number]: any[] }>({})
  const [rootFolderExpanded, setRootFolderExpanded] = useState<boolean>(true)
  const [search, setSearch] = useState<string>("")
  const [isShow, setIsShow] = useState<boolean>(false)
  const [recentDeletedFolder, setRecentDeletedFolder] = useState<number>(0)
  const [showBatchSelection, setShowBatchSelection] = useState(false)
  const [batchData, setBatchData] = useState<DictType[]>([])
  const [pageParam, setPageParam] = useState<number | null>(1)
  const [nextPage, setNextPage] = useState<number | null>(0)
  const [filterActive, setFilterActive] = useState(false)
  const [shareViaEmailModal, setShareViaEmailModal] = useState<boolean>(false)
  const [selectedFiles, setSelectedFiles] = useState<DictType[]>([])
  const [showDeleted, setShowDeleted] = useState(false)
  const [view, setView] = useState(0)
  const isSubFolderRef = useRef(false)
  const isOrganizationOwner = authConfig?.workspace?.owner_id === authConfig?.id
  const isAdmin = authConfig?.companies_of_user?.find((company: any) => {
    if (company?.id === authConfig?.last_organization_id && company?.role?.id == 22 /* ADMIN_ROLE_ID */) return true
  })

  const sortingBy = useMemo(
    () => [
      { value: "asc", label: "Ascending" },
      { value: "desc", label: "Descending" },
    ],
    [],
  )
  const sortingField = useMemo(
    () => [
      { value: "name", label: "Name" },
      { value: "created_at", label: "Date Created" },
      { value: "last_update", label: "Date Modified" },
      { value: "size", label: "Size" },
    ],
    [],
  )
  const imageExtensions = ["png", "webp", "jpeg", "jpg"]

  const { translate, locale } = useTranslator()
  const [hoveredFolderId, setHoveredFolderId] = useState<string | null>(null)
  const [previousFolderItems, setPreviousFolderItems] = useState<{ [id: number]: any[] }>({})
  const [cutFiles, setCutFiles] = useState<DictType[]>([])
  const [copyFiles, setCopyFiles] = useState<DictType[]>([])
  const [moveFileModal, setMoveFileModal] = useState<boolean>(false)
  const [isMenuView, setIsMenuView] = useState<boolean>(false)
  const [selectedMoveFile, setSelectedMoveFile] = useState<{}>({})
  const [recentDeletedFile, setRecentDeletedFile] = useState<any[]>([])
  const [preFolderItems, setPreFolderItems] = useState<{ [id: number]: any[] }>({})
  const [folderId, setFolderId] = useState<number>(0)
  const printAble = ["jpg", "jpeg", "png", "pdf"]
  const [sortingObj, setSortingObj] = useState({ sorting: "name", order: "asc" })
  const [fileTypes, setFileTypes] = useState<string[]>([])
  const [clearFilter, setClearFilter] = useState<boolean>(false)
  const [newActiveData, setNewActiveData] = useState<any>({})

  const isHebrew = locale === "he"
  const checkboxRef = useRef<HTMLInputElement | undefined>()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const navigate = useNavigate()
  const observerRef = useRef(null)
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)
  const queryKey = searchParam.get("revalidate")
  const queryAction = searchParam.get("action")

  const moveFile = async (params: { files_id: number[]; folder_id: number }) => {
    const response = await axios.post(SERVER_URL + MOVE_FILE_ENDPOINT, params, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    return response.data
  }

  const refetchFolderContent = (folderId: number) => {
    queryClient.invalidateQueries({
      queryKey: [{ id: folderId }, null, "get-curr-folder-files"],
    })
  }

  const moveFileMutation = useMutation({
    mutationKey: ["move-file"],
    mutationFn: moveFile,
    onSuccess: (data, variables, context) => {
      const fileIds = variables.files_id
      const sourceFolderIds = new Set<number>()
      let singleFileName: string | undefined
      if (fileIds.length === 1) {
        singleFileName = cutFiles.find((file) => file.id === fileIds[0])?.name
      }

      fileIds.forEach((fileId: number) => {
        const sourceFolder = foldersMeta[fileId]
        if (sourceFolder) {
          sourceFolderIds.add(sourceFolder)
        }
      })

      // Optimistically update folderItems to remove all moved files from their source folders
      sourceFolderIds.forEach((sourceFolder) => {
        setFolderItems((prev) => ({
          ...prev,
          [sourceFolder]: prev[sourceFolder].filter((item) => !fileIds.includes(item.id)),
        }))
        refetchFolderContent(sourceFolder)
      })

      const targetFolderId = variables.folder_id
      const targetFolder = Object.values(folderItems)
        .flat()
        .find((item) => item.id === targetFolderId && item.as === "folder")

      if (targetFolderId) {
        refetchFolderContent(targetFolderId)
      }
      if (targetFolder) {
        handleTreeItemFolderClick(targetFolder, true)
      }

      if (data.id == rootFolder.id) {
        handleTreeItemFolderClick(data, true)
      }
      resetPageParam()

      toast({
        title: translate("Success"),
        description: fileIds.length > 1
          ? `${fileIds.length} ${translate("files")} ${translate("have been moved")}`
          : `${singleFileName ?? "File"} ${translate("has been moved")}`,
        variant: "success",
      })

      setCutFiles([])

      newParams.set("revalidate", "projects")
      navigate(`?${newParams.toString()}`)
    },
    onError: (error) => {
      const fileIds = moveFileMutation.variables?.files_id || []
      setFolderItems(previousFolderItems)
      toast({
        title: translate("Error"),
        description: fileIds.length > 1
          ? `${translate("Unable to move")} ${fileIds.length} ${translate("files")}`
          : `${translate("Unable to move")} ${cutFiles.find((file) => file.id === fileIds[0])?.name ?? "File"
          }`,
        variant: "error",
      })
    },
  })

  const handlePasteFiles = () => {
    if (cutFiles.length === 0 || !activeFolder?.id) return
    const folderId = cutFiles[0]?.folders_id

    const fileIds = cutFiles.map((file) => file.id)
    const newFolderItems = {
      ...folderItems,
      [folderId]: folderItems[folderId].filter((file) => !fileIds.includes(file.id)),
    }

    setFolderItems(newFolderItems)
    moveFileMutation.mutate({
      files_id: fileIds,
      folder_id: activeFolder.id,
    })
  }

  const getModifiedFileName = (originalName: string): string => {
    const dotIndex = originalName.lastIndexOf(".")
    if (dotIndex === -1) return originalName + " - Copy"

    const name = originalName.substring(0, dotIndex)
    const extension = originalName.substring(dotIndex)
    return `${name} - Copy${extension}`
  }

  const fetchAndCreateFile = async (
    url: string,
    originalName: string,
    condition: boolean,
  ): Promise<File> => {
    const response = await fetch(url)
    const blob = await response.blob()
    const modifiedName = condition ? getModifiedFileName(originalName) : originalName
    return new window.File([blob], modifiedName, { type: blob.type })
  }

  const handlePateCopyFiles = async () => {
    const condition = copyFiles.some((file) => foldersMeta[file.id] === activeFolder.id)
    const filePromises = copyFiles.map((item) =>
      fetchAndCreateFile(item.file, item.name, condition),
    )
    const fileObjects = await Promise.all(filePromises)
    setIsShow(true)
    uploadFileMutation.mutate(fileObjects)
  }

  const uploadFile = async (files: File[]) => {
    const formData = new FormData()

    if (!files || files.length === 0) {
      return
    }

    files.forEach((file: string | Blob) => {
      formData.append("files", file)
    })

    const url = `${DIGITAL_OCEAN_FILES_ENDPOINT}?token=${accessToken}`

    const response = await axios.post(url, formData, {
      headers: {
        accept: "*/*",
        "Content-Type": "multipart/form-data",
      },
    })

    return response?.data
  }

  const uploadFileMutation = useMutation({
    mutationKey: ["upload-file"],
    mutationFn: uploadFile,
    onSuccess: (data) => {
      const payload = data as ProjectFile[]
      setCopyFiles([])
      uploadToProjectMutation.mutate(payload)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      setIsShow(false)
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to copy file at this time"),
        variant: "error",
      })
    },
  })

  const uploadToProject = async (files: ProjectFile[]) => {
    const file_ids = files?.map((f) => {
      return f.id
    })
    const body = activeFolder?.isRoot
      ? {
        project_id: project?.id,
      }
      : {
        folder_id: activeFolder?.id,
      }
    const response = await axios.post(
      SERVER_URL + ADD_FILES_TO_PROJECT_ENDPOINT,
      {
        ...body,
        files_id: file_ids,
        inform_users: true,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const uploadToProjectMutation = useMutation({
    mutationKey: ["add-files-to-project", project?.id],
    mutationFn: uploadToProject,
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: translate("Project file Copied"),
        variant: "success",
      })
      refetch()
      setIsShow(false)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to copy file at this time"),
        variant: "error",
      })
    },
  })

  const handleClearCutFunctionality = () => {
    setCutFiles([])
    setFolderItems((pre) => ({ ...pre, [`${folderId}`]: preFolderItems[`${folderId}`] }))
    setPreFolderItems({})
    setFolderId(0)
  }

  const handleClearCopyFunctionality = () => setCopyFiles([])

  const treeSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  )

  const infiniteSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  )

  const SortableFileItem = ({
    file,
    children,
  }: {
    file: DictType
    children: (args: {
      dragHandleProps: {
        listeners: ReturnType<typeof useSortable>["listeners"]
        attributes: ReturnType<typeof useSortable>["attributes"]
      }
    }) => React.ReactNode
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: `file_${file.id}`,
      data: { type: "File", file },
    })
    const style = {}

    return (
      <div ref={setNodeRef} style={style}>
        {children({ dragHandleProps: { listeners, attributes } })}
      </div>
    )
  }

  const SortableFolderItem = ({
    folder,
    children,
  }: {
    folder: DictType
    children: React.ReactNode
  }) => {
    const { attributes, listeners, setNodeRef } = useSortable({
      id: `folder_${folder.id}`,
      data: { type: "Folder", folder },
    })

    return (
      <div ref={setNodeRef} {...attributes} {...listeners}>
        {children}
      </div>
    )
  }

  const DraggableItem = ({ item, children }: { item: DictType; children: React.ReactNode }) => {
    const isFile = item.file

    const {
      attributes: dragAttributes,
      listeners: dragListeners,
      setNodeRef: dragRef,
      transform,
    } = useDraggable({
      id: isFile ? `file_infinite_${item.id}` : `folder_${item.id}`,
      data: isFile ? { type: "File", file: item } : { type: "Folder", folder: item },
      disabled: !isFile,
    })

    const { setNodeRef: dropRef, isOver } = useDroppable({
      id: `folder_${item.id}`,
      data: { type: "Folder", folder: item },
      disabled: isFile,
    })

    // Combine refs for the same element
    const setNodeRef = isFile ? dragRef : dropRef

    const style = {}
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...(isFile ? { ...dragAttributes, ...dragListeners } : {})}
        data-draggable-id={isFile ? `file_infinite_${item.id}` : `folder_${item.id}`}
        className={cn("relative", isOver && !isFile ? "bg-accent" : "")}
      >
        {children}
      </div>
    )
  }

  function onDragStartTree(event: DragStartEvent) {
    if (event.active.data.current?.type === "File") {
      const file = event.active.data.current.file
      setActiveFileTree(file)
    }
  }

  function onDragEndTree(event: DragEndEvent) {
    const { active, over } = event
    setActiveFileTree(null)
    if (!over || active.id === over.id) return

    const activeId = active.id.toString()
    const activeFolder = active?.data?.current?.file?.folders_id
    const overId = over.id.toString()

    if (activeId.startsWith("file_") && overId.startsWith("folder_")) {
      const fileId = parseInt(activeId.split("_")[1])
      const folderId = parseInt(overId.split("_")[1])
      const file = Object.values(folderItems)
        .flat()
        .find((item) => item.id === fileId && item.as === "file")
      const targetFolder = Object.values(folderItems)
        .flat()
        .find((item) => item.id === folderId && item.as === "folder")

      if (file && folderId == rootFolder.id) {
        const newFolderItems = {
          ...folderItems,
          [folderId]: [...folderItems[folderId], file],
          [activeFolder]: folderItems[activeFolder].filter((file) => file.id != fileId),
        }
        handleFolderState(newFolderItems, file.id, folderId)
      } else {
        if (file && targetFolder && file.folders_id !== targetFolder?.id) {
          const newFolderItems = {
            ...folderItems,
            [activeFolder]: folderItems[activeFolder].filter((file) => file.id != fileId),
          }

          if (folderItems[folderId] !== undefined)
            newFolderItems[folderId] = [...folderItems[folderId], file]

          handleFolderState(newFolderItems, file.id, targetFolder.id)
        }
      }
    }

    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
  }

  function handleFolderState(
    newFolderItems: { [id: number]: any[] },
    fileId: number,
    folderId: number,
  ) {
    setPreviousFolderItems(folderItems)
    setFolderItems(newFolderItems)
    moveFileMutation.mutate({ files_id: [fileId], folder_id: folderId })
  }

  function onDragStartInfinite(event: DragStartEvent) {
    if (event.active.data.current?.type === "File") {
      const file = event.active.data.current.file
      setActiveFileInfinite(file)
      setHoveredFolderId(null)
    }
  }

  function onDragOverInfinite(event: any) {
    const { active, over } = event

    if (!over) {
      setHoveredFolderId(null)
      return
    }

    const overId = over.id.toString()

    if (overId.startsWith("folder_")) {
      setHoveredFolderId(overId)
    } else {
      setHoveredFolderId(null)
    }
  }

  function onDragEndInfinite(event: DragEndEvent) {
    const { active, over } = event
    setActiveFileInfinite(null)
    setHoveredFolderId(null)

    if (!over || active.id === over.id) return

    const activeId = active.id.toString()
    const activeFolder = active?.data?.current?.file?.folders_id
    const overId = over.id.toString()

    if (activeId.startsWith("file_infinite_") && overId.startsWith("folder_")) {
      const fileId = parseInt(activeId.split("_")[2])
      const folderId = parseInt(overId.split("_")[1])

      const file = Object.values(folderItems)
        .flat()
        .find((item) => item.id === fileId && item.as === "file")
      const targetFolder = Object.values(folderItems)
        .flat()
        .find((item) => item.id === folderId && item.as === "folder")

      if (file && targetFolder) {
        if (folderItems[folderId] == undefined) {
          const newFolderItems = {
            ...folderItems,
            [activeFolder]: folderItems[activeFolder].filter((file) => file.id != fileId),
          }
          setFolderItems(newFolderItems)
        } else {
          const newFolderItems = {
            ...folderItems,
            [folderId]: [...folderItems[folderId], file],
            [activeFolder]: folderItems[activeFolder].filter((file) => file.id != fileId),
          }
          setFolderItems(newFolderItems)
        }
        moveFileMutation.mutate({
          files_id: [file.id],
          folder_id: targetFolder.id,
        })
      }
    }
  }

  function handleDragOverTree(event: any) {
    const { active, over } = event
    if (!over) return

    const overId = over.id.toString()
    if (overId.startsWith("folder_")) {
      const folderId = parseInt(overId.split("_")[1])
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current)
      }
    } else {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current)
        hoverTimerRef.current = null
      }
    }
  }

  const getFolderContent = async (folderId: number) => {
    const payload: any = { folder_id: folderId }

    if (search) {
      payload["file_name_filter"] = search
      payload["subfolder_name_filter"] = search
    }

    if (sortingObj?.sorting != "" && sortingObj?.order) {
      payload["file_sorting"] = { orderBy: sortingObj?.order, sortBy: sortingObj?.sorting }
      payload["folder_sorting"] = {
        orderBy: sortingObj?.order,
        sortBy: sortingObj?.sorting == "size" ? "folder_size" : sortingObj?.sorting,
      }
    }

    if (showDeleted) payload["show_deleted"] = showDeleted

    if (fileTypes.length) payload["file_type_filter"] = fileTypes

    const response = await axios.post(`${SERVER_URL}${FOLDER_ENDPOINT}`, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    isSubFolderRef.current = false
    return response.data
  }

  const {
    data: folderContent,
    isLoading,
    isFetching,
    isFetched,
    refetch,
  } = useQuery({
    queryKey: [activeFolder, "get-curr-folder-files", showDeleted],
    queryFn: () => getFolderContent(activeFolder?.id),
    enabled: !!activeFolder?.id,
  })

  const handleLoadMoreData = () => {
    if (nextPage) {
      setPageParam(nextPage)
    }
  }

  useEffect(() => {
    if (queryKey === "projects") {
      if (rootFolder.id == activeFolder.id) {
        refetch()
      } else {
        if (queryAction === "delete") {
          if (breadcrumbs[activeFolder.id].length > 1) {
            const parent = breadcrumbs[activeFolder.id][breadcrumbs[activeFolder.id].length - 2]
            setActiveFolder(parent)
            setPageParam(1)
            isSubFolderRef.current = true
            refetch()
          } else {
            closeModal && closeModal()
          }
        } else {
          refetch()
        }
      }
      navigate(`${location.pathname}`)
    }
  }, [queryKey, queryAction])

  function collapseSubfolders(data: any, folderId: number, includeSelf: boolean = false) {
    // If includeSelf is true, collapse the folderId itself
    if (includeSelf) {
      for (const key in data) {
        data[key].forEach((item: any) => {
          if (item.as === "folder" && item.id === folderId) {
            item.isExpanded = false
          }
        })
      }
    }

    function recurse(currentId: number): void {
      const children = data[currentId]
      if (!children) return

      for (const item of children) {
        if (item.as === "folder") {
          if (item.isExpanded) {
            item.isExpanded = false
          }
          recurse(item.id)
        }
      }
    }

    recurse(folderId)

    return data
  }

  const handleTreeItemFolderClick = (item: DictType, forceExpand: boolean = false) => {
    const parentId: number = foldersMeta[item.id]
    const isCurrentlyExpanded = item.id === rootFolder.id ? rootFolderExpanded : item.isExpanded
    const willExpand = forceExpand || !isCurrentlyExpanded

    if (willExpand) {
      // Trigger API call on expansion
      isSubFolderRef.current = true
      setPageParam(1)
      setActiveFolder(item)
      if (activeFolder?.id === item.id && !forceExpand) {
        refetch()
      }
    } else setActiveFolder(item)

    if (parentId) {
      // Update folder expansion state
      if (willExpand == false) {
        const data = collapseSubfolders(folderItems, item.id, true)
        setFolderItems(data)
      } else {
        setFolderItems((prev) => {
          const parentItems = prev[parentId]
          return {
            ...prev,
            [parentId]: parentItems.map((_item) => {
              if (_item.id === item.id) {
                return { ..._item, isExpanded: forceExpand || !_item.isExpanded }
              }
              return _item
            }),
          }
        })
      }
    } else if (item.id === rootFolder.id) {
      // Update root folder expansion state
      if (willExpand == false) {
        const data = collapseSubfolders(folderItems, item.id, true)
        setFolderItems(data)
      }
      setRootFolderExpanded(forceExpand || !rootFolderExpanded)
    }

    setBatchData([])
    setShowBatchSelection(false)
  }

  const resetPageParam = () => setPageParam(1)

  const renderTreeItems = (item: DictType) => {
    switch (item.as) {
      case "folder":
        return renderTreeItemFolder(item)
      case "file":
        return renderTreeItemFile(item)
      default:
        return null
    }
  }

  const renderTreeItemFolder = (item: DictType) => {
    const isExpanded = item.id === rootFolder.id ? rootFolderExpanded : item.isExpanded
    const isActive = activeFolder.id === item.id

    return (
      <SortableFolderItem folder={item}>
        <div key={item.id} className='folder relative flex flex-col gap-1'>
          <div
            className='flex w-full items-center justify-between'
            style={
              isHebrew
                ? { paddingRight: `${item.level + 1}rem` }
                : { paddingLeft: `${item.level + 1}rem` }
            }
          >
            <button onClick={() => handleTreeItemFolderClick(item)} className='flex-1'>
              <div
                className={`flex items-center justify-between gap-1 rounded-md p-1 outline-none transition-colors hover:bg-muted hover:text-black ${item.isExpanded && "text-accent-foreground"} ${activeFolder.id === item.id && "bg-blue-600 text-white"}`}
              >
                <div className='flex items-center gap-1'>
                  {isExpanded ? (
                    <FolderOpenIcon
                      className={`h-5 w-5 text-gray-500 ${activeFolder.id === item.id && "text-white"}`}
                    />
                  ) : (
                    <FolderIcon
                      className={`h-5 w-5 text-gray-500 ${activeFolder.id === item.id && "text-white"}`}
                    />
                  )}
                  <div
                    className={cn(
                      "flex flex-1 items-center gap-1 truncate",
                      item.deleted ? "text-red-500" : "",
                    )}
                  >
                    {item?.name}
                  </div>
                </div>
                {isActive && (isFetching || isLoading) ? (
                  <div className='dots-loader pr-3'>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='h-6 w-6 p-0 hover:bg-white'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal
                          className={cn(
                            "h-4 w-4",
                            activeFolder.id === item.id ? "text-white" : "text-gray-500",
                          )}
                        />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-fit p-2'>
                      {item.deleted &&
                        (isOrganizationOwner || isAdmin || item.deleted_by == authConfig.id) ? (
                        <div className='flex flex-col gap-2' onClick={(e) => e.stopPropagation()}>
                          <RestoreFolder
                            folder={item}
                            setFolderItems={setFolderItems}
                            isMenuView={true}
                            revalidate={refetch}
                            refetchAllFolder={refetchAllFolder}
                            setActiveFolder={setActiveFolder}
                          />
                        </div>
                      ) : (
                        !item.deleted ?
                          <div className='flex flex-col gap-2'>
                            <button
                              className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                              onClick={(e) => e.stopPropagation()}
                            >
                              <AddSubFolder
                                parent={item}
                                revalidate={refetch}
                                isMenuView={true}
                                type={0}
                                refetchAllFolder={refetchAllFolder}
                              />
                            </button>
                            {item.id !== rootFolder.id ? (
                              <button
                                className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                                onClick={(e) => e.stopPropagation()}
                              >
                                <DeleteFolder
                                  folder={item}
                                  isMenuView={true}
                                  setFolderItems={setFolderItems}
                                  foldersMeta={foldersMeta}
                                  setIsShow={setIsShow}
                                  breadcrumbs={breadcrumbs}
                                  setBreadcrumbs={setBreadcrumbs}
                                  handleTreeItemFolderClick={handleTreeItemFolderClick}
                                  setFoldersMeta={setFoldersMeta}
                                  setRecentDeletedFolder={setRecentDeletedFolder}
                                  refetchAllFolder={refetchAllFolder}
                                />
                              </button>
                            ) : null}
                            {item.level > 0 ? (
                              <button
                                className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                                onClick={(e) => e.stopPropagation()}
                              >
                                <AddSubFolder
                                  parent={item}
                                  revalidate={refetch}
                                  isMenuView={true}
                                  type={1}
                                  folderItems={folderItems}
                                  setFolderItems={setFolderItems}
                                  setActiveFolder={setActiveFolder}
                                  setBreadcrumbs={setBreadcrumbs}
                                />
                              </button>
                            ) : null}
                          </div> : null
                      )}
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </button>
          </div>
          {isExpanded && (
            <div
              className='vertical-line absolute z-10'
              style={{
                top: "2.5rem",
                width: "2px",
                height: `calc(100% - 3.5rem)`,
                backgroundColor: "#d1d5db",
                ...(isHebrew
                  ? { right: `${item.level + 1}rem` }
                  : { left: `${item.level + 1}rem` }),
              }}
            />
          )}
          <Separator />
          {isExpanded && folderItems[item.id] && (
            <div className='folder-contents'>
              {folderItems[item.id]
                .sort((a, b) => (a.as === "folder" && b.as !== "folder" ? -1 : 1))
                .map((child) => (
                  <div key={child.id} className='relative'>
                    <div
                      className='horizontal-line absolute'
                      style={{
                        ...(isHebrew
                          ? { right: `${item.level + 1}rem` }
                          : { left: `${item.level + 1}rem` }),
                        top: "1.05rem",
                        width: "1rem",
                        height: "2px",
                        backgroundColor: "#d1d5db",
                        transform: "translateY(-50%)",
                      }}
                    />
                    {renderTreeItems({ ...child, level: item.level + 1 })}
                  </div>
                ))}
            </div>
          )}
        </div>
      </SortableFolderItem>
    )
  }

  const handleFileType = (file: DictType) => {
    const fileName = file?.name.replace(/\.[^/.]+$/, "").toLowerCase()
    if (fileName == file?.type?.toLowerCase()) return "txt"
    else if (file?.type == "") return "txt"
    else return file?.type
  }

  const handleShowTask = (item: any) => {
    return item?.task_id != 0 ? (
      <div
        className='mt-1 cursor-pointer rounded-full bg-blue-100 px-2 py-1 text-[10px] font-bold'
        onClick={() => navigate(`${PROJECTS_PAGE_ROUTE}/${item.project_id}`)}
      >
        PR-{item.task_id}
      </div>
    ) : item?.diary_id != 0 ? (
      <div
        className='mt-1 cursor-pointer rounded-full bg-blue-100 px-2 py-1 text-[10px] font-bold'
        onClick={() => navigate(`${PROJECTS_PAGE_ROUTE}/${item.project_id}`)}
      >
        WD-{item.diary_id}
      </div>
    ) : null
  }

  const renderTreeItemFile = (item: DictType) => {
    return (
      <SortableFileItem file={item} key={item.id}>
        {({ dragHandleProps }) => (
          <FilePreview file={item}>
            <div
              className='flex w-full items-center'
              style={
                isHebrew
                  ? { paddingRight: `${item.level + 1}rem` }
                  : { paddingLeft: `${item.level + 1}rem` }
              }
            >
              <button className='flex w-full items-center justify-between gap-2 rounded-md p-1 outline-none transition-colors hover:bg-muted'>
                <div className='flex w-full flex-1 items-center gap-2 overflow-hidden'>
                  <RenderFileIcon type={handleFileType(item)} />
                  <div
                    className={cn("w-[80%] truncate text-left", item.deleted ? "text-red-500" : "")}
                  >
                    {item?.name}
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  <div className='text-[8px] text-muted-foreground'>
                    {convertFileSize(item.size)}
                  </div>

                  <GripVertical
                    className='cursor-grab text-gray-500'
                    size={20}
                    {...dragHandleProps.listeners}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='h-6 w-6 p-0 hover:bg-white'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className='h-4 w-4 text-gray-500' />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-fit p-2'>
                      <div className='flex flex-col gap-2'>
                        <button
                          className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                          onClick={(e) => {
                            e.stopPropagation()
                            downloadFile(item)
                          }}
                        >
                          <div className='px-2'>
                            <Download size={16} />
                          </div>
                          <span className='text-sm'>{translate("Download File")}</span>
                        </button>
                        <button
                          className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                          onClick={(e) => {
                            e.stopPropagation()
                            setIsMenuView(true)
                            setSelectedMoveFile(item)
                            setMoveFileModal(!moveFileModal)
                          }}
                        >
                          <div className='flex w-full items-center gap-2'>
                            <div className='px-2'>
                              <FolderSearch className='' size={16} />
                            </div>
                            <span className='text-sm'>{`${translate("Move file")}`}</span>
                          </div>
                        </button>
                        <button
                          className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CutFile
                            file={item}
                            folderItems={folderItems}
                            activeFolder={activeFolder}
                            setCutFiles={setCutFiles}
                            setPreFolderItems={setPreFolderItems}
                            setFolderId={setFolderId}
                            setFolderItems={setFolderItems}
                            isMenuView={true}
                          />
                        </button>
                        <button
                          className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CopyFile file={item} setCopyFiles={setCopyFiles} isMenuView={true} />
                        </button>
                        <button
                          className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ShareFileOnWhatsApp file={item} isMenuView={true} />
                        </button>
                        <button
                          className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedFiles([item])
                            setShareViaEmailModal(true)
                          }}
                        >
                          <div className='flex items-center gap-2 rounded hover:bg-muted'>
                            <div className='px-2'>
                              <Mail className='' size={16} />
                            </div>
                            <span className='text-sm'>{translate("Share on Mail")}</span>
                          </div>
                        </button>
                        {printAble.includes(item?.type) ? (
                          <button
                            className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                            onClick={(e) => e.stopPropagation()}
                          >
                            <PrintFile file={item} isMenuView={true} />
                          </button>
                        ) : null}
                        {item.deleted &&
                          (isOrganizationOwner || isAdmin || item.deleted_by == authConfig.id) ? (
                          <button
                            className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                            onClick={(e) => e.stopPropagation()}
                          >
                            <RestoreFile file={item} isMenuView={true} />
                          </button>
                        ) : null}

                        <button
                          className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DeleteFile
                            file={item}
                            isMenuView={true}
                            setFolderItems={setFolderItems}
                            setBreadcrumbs={setBreadcrumbs}
                            foldersMeta={foldersMeta}
                            setRecentDeletedFile={setRecentDeletedFile}
                            refetchFolderContent={refetchFolderContent}
                          />
                        </button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </button>
            </div>
          </FilePreview>
        )}
      </SortableFileItem>
    )
  }

  useEffect(() => {
    if (isFetched && folderContent) {
      const { files, subfolders } = folderContent
      const folderMetaData: { [id: number]: number } = { ...foldersMeta }
      const newBreadcrumbs = { ...breadcrumbs }
      const rootFolderFiles =
        files
          ?.map((item: DictType) => {
            if (!recentDeletedFile.includes(item.id)) {
              folderMetaData[item?.id] = activeFolder?.id
              return { ...item, as: "file", isExpanded: false }
            }
          })
          .filter((item: DictType) => item != undefined) || []
      const rootFolderSubFolders =
        subfolders
          ?.map((item: DictType) => {
            if (item.id != recentDeletedFolder) {
              folderMetaData[item?.id] = activeFolder?.id
              newBreadcrumbs[item?.id] = [...(newBreadcrumbs[activeFolder?.id] || []), item]
              return { ...item, as: "folder", isExpanded: false }
            }
          })
          .filter((item: DictType) => item != undefined) || []

      setFoldersMeta(folderMetaData)
      setBreadcrumbs(newBreadcrumbs)
      setRecentDeletedFolder(0)
      setRecentDeletedFile([])

      setFolderItems((prev) => {
        const existingItems = prev[activeFolder?.id] || []
        const newItems = [...rootFolderSubFolders, ...rootFolderFiles]
        const cutFileIds = new Set(cutFiles.map((file) => file.id))

        // Append new items, avoiding duplicates and filtering out moved files
        const updatedItems = [
          ...existingItems.filter(
            (item) =>
              !cutFileIds.has(item.id) && !newItems.some((newItem) => newItem.id === item.id),
          ),
          ...newItems.filter((item) => !cutFileIds.has(item.id)),
        ]

        return {
          ...prev,
          [activeFolder?.id]: newItems,
        }
      })
      setNextPage(files.nextPage || null)
      if (clearFilter) setClearFilter(false)
      if (filterActive) setFilterActive(false)
    }
  }, [isFetched, folderContent])

  const treeRef = useRef<any[]>([])

  const generateFolderTree = () => {
    const renderFolderTreeItems = (folder: DictType, level: number, isExpanded: boolean) => {
      const items: any[] = []
      items.push(renderTreeItems({ ...folder, level, as: "folder" }))
      return items
    }

    const tree = renderFolderTreeItems({ ...rootFolder, as: "folder" }, 0, rootFolderExpanded)

    // Remove duplicates while preserving order
    const uniqueTree = Array.from(
      new Map(tree.map((item) => [parseInt(item?.key || ""), item])).values(),
    )

    treeRef.current = uniqueTree
    return uniqueTree
  }

  const allItemIds = useMemo(() => {
    const ids: string[] = []
    Object.values(folderItems)
      .flat()
      .forEach((item) => {
        if (item.as === "folder") {
          ids.push(`folder_${item.id}`)
        }
        if (item.as === "file") {
          ids.push(`file_${item.id}`)
        }
      })
    return ids
  }, [folderItems])

  if (!rootFolder?.id) {
    return <PmsLoader />
  }

  const handleBatchSelection = () => {
    setBatchData([])
    setShowBatchSelection(!showBatchSelection)
  }

  const selectAllFiles = () => {
    const arr = folderItems[activeFolder?.id].map((item: DictType) => item)

    setBatchData([...arr])
  }

  const clearSelection = () => {
    setBatchData([])
  }

  const handleSelection = (selected: DictType, event: SyntheticEvent) => {
    const { checked } = event.target as HTMLInputElement

    if (checked) {
      const arr = []
      arr.push(selected, ...batchData)
      setBatchData(arr)
    } else if (!checked) {
      const removed = batchData.filter((val: any) => val.id !== selected.id)
      setBatchData(removed)
    }
  }

  const handleClick = (item: any) => {
    if (item) {
      const ch = document.getElementById(`check-${item.id}`)
      ch?.click()
    }
  }

  const renderTotalFiles = () => {
    const exitsFolder = folderItems[activeFolder.id]
    if (exitsFolder == undefined) return `0 ${translate("Items")}`

    return `${exitsFolder.length || 0} ${translate("Items")}`
  }

  const downloadFile = (file: any) => {
    axios({
      url: file?.file,
      method: "GET",
      responseType: "blob",
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url

      // Optional: use file name if available, else fallback
      const fileName = file?.name || "downloaded_file"
      link.setAttribute("download", fileName)
      document.body.appendChild(link)
      link.click()

      // Clean up
      link.remove()
      window.URL.revokeObjectURL(url)
    })
  }

  const handleSearch = useCallback(() => {
    refetch()
  }, [])

  function debounce<T extends (...args: any[]) => any>(
    func: T,
    delay: number,
  ): ((...args: Parameters<T>) => void) & { cancel: () => void } {
    let timeoutId: NodeJS.Timeout | null = null

    const debounced = function (this: any, ...args: Parameters<T>) {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        func.apply(this, args)
        timeoutId = null
      }, delay)
    }

    debounced.cancel = () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    return debounced as ((...args: Parameters<T>) => void) & { cancel: () => void }
  }

  const debounceFunction = useCallback(debounce(handleSearch, 300), [handleSearch])

  useEffect(() => {
    debounceFunction()
  }, [search])

  useEffect(() => {
    if (fileTypes) refetch()
  }, [fileTypes])

  useEffect(() => {
    if (sortingObj?.sorting != "" && sortingObj?.order) refetch()
  }, [sortingObj])

  const clearFilterValue = () => {
    setSearch("")
    setSortingObj({ sorting: "name", order: "asc" })
    setFileTypes([])
  }

  const RenderFilter = () => {
    const searchArray: string[] = []

    if (search != "") searchArray.push(search)
    if (fileTypes.length > 0) searchArray.push(...fileTypes)

    return searchArray.length > 0 ? (
      <div className='flex items-center gap-1 p-2 transition-all ease-in-out'>
        <div className='flex items-center gap-2'>
          <small>{translate("Active filter")}:</small>{" "}
          {searchArray.map((searchItem: string, index) => (
            <Badge
              variant={index % 2 === 1 ? "outline" : "default"}
              className='font-thin'
              key={index}
            >
              {`${translate(searchItem)}`}
            </Badge>
          ))}
        </div>
        <X size={16} className='cursor-pointer text-red-500' onClick={clearFilterValue} />
      </div>
    ) : null
  }

  useEffect(() => {
    if (clearFilter) clearFilterValue()
    else {
      if (Object.keys(newActiveData).length > 0) handleTreeItemFolderClick(newActiveData, true)
      else setNewActiveData({})
    }
  }, [clearFilter])

  useEffect(() => {
    if (search != "" || fileTypes.length != 0) setFilterActive(true)
    else setFilterActive(false)
  }, [search, fileTypes])

  return (
    <Fragment>
      <div className='my-1 flex items-center justify-end'>
        <div className='flex items-center gap-2'>
          <Switch
            checked={showDeleted}
            className='h-6 w-11'
            onCheckedChange={(checked) => setShowDeleted(checked)}
          />
          <h4 className='text-sm font-semibold'>{translate("Show Deleted Files and Folders")}</h4>
        </div>
      </div>
      <div></div>
      {isShow ? (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
          <div className='h-16 w-16 animate-spin rounded-full border-4 border-white border-t-transparent'></div>
        </div>
      ) : null}
      <Card
        className={cn(
          "relative flex h-full w-full flex-row gap-2 overflow-hidden py-3",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <DndContext
          sensors={treeSensors}
          collisionDetection={pointerWithin}
          onDragStart={onDragStartTree}
          onDragEnd={onDragEndTree}
          onDragOver={handleDragOverTree}
        >
          <div className='hidden w-[25%] overflow-y-scroll border-r p-4 px-1 md:block md:w-[50%] lg:w-[30%] xl:w-[50%]'>
            <SortableContext items={allItemIds} strategy={verticalListSortingStrategy}>
              {generateFolderTree()}
            </SortableContext>
          </div>
          {createPortal(
            <DragOverlay>
              {activeFileTree && (
                <div className='flex items-center gap-2 rounded-md bg-white p-2 opacity-50 shadow-lg'>
                  <RenderFileIcon type={handleFileType(activeFileTree)} />
                  <span className='truncate text-sm'>{activeFileTree?.name}</span>
                </div>
              )}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>

        <div className='block h-auto truncate md:hidden'>
          <div>
            {openMobile && (
              <DndContext
                sensors={treeSensors}
                collisionDetection={closestCenter}
                onDragStart={onDragStartTree}
                onDragEnd={onDragEndTree}
                onDragOver={handleDragOverTree}
              >
                <div className='absolute left-0 right-0 top-0 w-full md:hidden'>
                  <Button
                    className='absolute -top-7 left-5 h-6 w-7'
                    size='icon'
                    variant={"outline"}
                    onClick={() => setOpenMobile(true)}
                  >
                    <MenuIcon />
                  </Button>
                  <Button
                    className='absolute -top-7 right-5 h-6 w-7'
                    size='icon'
                    variant={"destructive"}
                    onClick={() => setOpenMobile(false)}
                  >
                    <X />
                  </Button>
                  <SortableContext items={allItemIds} strategy={verticalListSortingStrategy}>
                    {generateFolderTree()}
                  </SortableContext>
                </div>
                {createPortal(
                  <DragOverlay>
                    {activeFileTree && (
                      <div className='flex items-center gap-2 rounded-md bg-white p-2 opacity-50 shadow-lg'>
                        <RenderFileIcon type={handleFileType(activeFileTree)} />
                        <span className='truncate text-sm'>{activeFileTree?.name}</span>
                      </div>
                    )}
                  </DragOverlay>,
                  document.body,
                )}
              </DndContext>
            )}
          </div>
        </div>

        <DndContext
          sensors={infiniteSensors}
          collisionDetection={pointerWithin}
          onDragStart={onDragStartInfinite}
          onDragOver={onDragOverInfinite}
          onDragEnd={onDragEndInfinite}
        >
          <div className='w-full gap-4'>
            <div className='mx-2 flex items-center gap-1 font-semibold'>
              {breadcrumbs[activeFolder?.id] && breadcrumbs[activeFolder?.id]?.length > 0 && (
                <>
                  {breadcrumbs[activeFolder.id].length > 1 && (
                    <Button
                      variant='link'
                      className='p-0'
                      onClick={() => {
                        handleTreeItemFolderClick(
                          breadcrumbs[activeFolder.id][breadcrumbs[activeFolder.id].length - 2],
                          true,
                        )
                      }}
                    >
                      <ChevronLeft />
                    </Button>
                  )}
                  {breadcrumbs[activeFolder.id].length > breadcrumsOverflowLimit && (
                    <Button variant={"disabled"} className='bg-transparent p-0'>
                      {"..."}
                    </Button>
                  )}
                  {breadcrumbs[activeFolder.id].map((bread, index) =>
                    index <=
                      breadcrumbs[activeFolder.id].length - 1 - breadcrumsOverflowLimit ? null : (
                      <Fragment key={bread.name + index}>
                        {index < breadcrumbs[activeFolder.id].length - 1 ? (
                          <>
                            {index > 0 && " / "}
                            <Button
                              variant={"link"}
                              className='p-0'
                              onClick={() => handleTreeItemFolderClick(bread, true)}
                            >
                              {bread.name ?? ""}
                            </Button>
                          </>
                        ) : (
                          <>
                            {index > 0 && " / "}
                            <Button variant={"disabled"} className='bg-transparent p-0'>
                              {bread.name ?? ""}
                            </Button>
                          </>
                        )}
                      </Fragment>
                    ),
                  )}
                </>
              )}
            </div>
            <div className='mx-2 flex flex-wrap justify-start gap-2'>
              <Input
                placeholder={`${translate("Search")}...`}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className='h-8 w-full md:w-64 lg:w-96'
              />
              <FileFilter
                files={folderItems[activeFolder?.id]}
                setFileTypes={setFileTypes}
                fileTypes={fileTypes}
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    className='w-full sm:w-auto'
                    size={"sm"}
                    leftSection={<ArrowUpDown size={14} />}
                  >
                    {translate("Sorting By")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-40'>
                  <div className='grid gap-4'>
                    <div className='space-y-2'>
                      {sortingField.map((field) => (
                        <RadioGroup
                          key={field.value}
                          className='flex cursor-pointer items-center space-x-2'
                        >
                          <RadioGroupItem
                            value={sortingObj?.sorting}
                            onClick={() =>
                              setSortingObj((pre) => ({ ...pre, sorting: field.value }))
                            }
                            checked={sortingObj?.sorting === field.value}
                            id={`field-${field.value}`}
                          />
                          <Label
                            className='cursor-pointer text-sm'
                            htmlFor={`field-${field.value}`}
                          >
                            {translate(field.label)}
                          </Label>
                        </RadioGroup>
                      ))}

                      <Separator className='my-2' />
                      {sortingBy.map((order) => (
                        <RadioGroup
                          key={order.value}
                          className='flex cursor-pointer items-center space-x-2'
                        >
                          <RadioGroupItem
                            value={sortingObj?.order}
                            onClick={() => setSortingObj((pre) => ({ ...pre, order: order.value }))}
                            checked={sortingObj?.order === order.value}
                            id={`file-${order.value}`}
                          />
                          <Label className='cursor-pointer' htmlFor={`file-${order.value}`}>
                            {translate(order.label)}
                          </Label>
                        </RadioGroup>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              {activeFolder?.id && (
                <>
                  {(cutFiles.length > 0 || copyFiles.length > 0) && (
                    <>
                      <Tooltip>
                        <TooltipTrigger>
                          <Button
                            variant='secondary'
                            size='icon'
                            onClick={() => {
                              if (cutFiles.length > 0) handlePasteFiles()
                              else if (copyFiles.length > 0) handlePateCopyFiles()
                            }}
                            disabled={
                              cutFiles.length > 0
                                ? !activeFolder?.id ||
                                cutFiles.some((file) => foldersMeta[file.id] === activeFolder.id)
                                : false
                            }
                            className='cursor-pointer'
                          >
                            <ClipboardList size={16} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className='bg-secondary'>
                          {translate("Paste")}{" "}
                          {cutFiles.length > 0 ? cutFiles.length : copyFiles.length}{" "}
                          {(cutFiles.length ? cutFiles.length : copyFiles.length) === 1
                            ? translate("file")
                            : translate("files")}
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger>
                          <Button
                            variant='destructive'
                            size='icon'
                            onClick={() => {
                              if (cutFiles.length > 0) handleClearCutFunctionality()
                              else if (copyFiles.length > 0) handleClearCopyFunctionality()
                            }}
                            className='flex items-center gap-2'
                          >
                            <ClipboardX size={16} />
                          </Button>
                          <TooltipContent className='bg-destructive'>
                            {translate(cutFiles.length > 0 ? "Clear Cut" : "Clear Copy")}
                          </TooltipContent>
                        </TooltipTrigger>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip>
                    <TooltipTrigger>
                      <Button variant='default' size='icon' onClick={handleBatchSelection}>
                        <FileCog size={16} />
                      </Button>
                      <TooltipContent>{translate("Batch actions")}</TooltipContent>
                    </TooltipTrigger>
                  </Tooltip>
                  <UploadFile
                    projectId={project?.id}
                    revalidate={() => {
                      if (pageParam == 1) refetch()
                      else setPageParam(1)
                    }}
                    folder={activeFolder}
                  />
                  <AddSubFolder
                    parent={activeFolder}
                    revalidate={refetch}
                    type={0}
                    refetchAllFolder={refetchAllFolder}
                  />
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        variant='ghost'
                        size={"icon"}
                        className='bg-slate-200/50'
                        onClick={() => {
                          setSelectedFiles([activeFolder])
                          setShareViaEmailModal(true)
                        }}
                      >
                        <MailQuestion className='text-primary' size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className='bg-primary'>
                      {translate("Email folder content")}{" "}
                    </TooltipContent>
                  </Tooltip>
                  {/* <EmailFolderFiles folder={activeFolder} /> */}
                  {activeFolder.id != rootFolder.id &&
                    activeFolder.deleted &&
                    (isOrganizationOwner || isAdmin || activeFolder.deleted_by == authConfig.id) ? (
                    <RestoreFolder
                      setFolderItems={setFolderItems}
                      revalidate={refetch}
                      setActiveFolder={setActiveFolder}
                      folder={activeFolder}
                      refetchAllFolder={refetchAllFolder}
                    />
                  ) : null}
                  {activeFolder.id != rootFolder.id ? (
                    <AddSubFolder
                      parent={activeFolder}
                      revalidate={refetch}
                      type={1}
                      folderItems={folderItems}
                      setFolderItems={setFolderItems}
                      setActiveFolder={setActiveFolder}
                      setBreadcrumbs={setBreadcrumbs}
                    />
                  ) : null}
                  {activeFolder.id != rootFolder.id ? (
                    <DeleteFolder
                      folder={activeFolder}
                      setFolderItems={setFolderItems}
                      foldersMeta={foldersMeta}
                      setIsShow={setIsShow}
                      breadcrumbs={breadcrumbs}
                      setBreadcrumbs={setBreadcrumbs}
                      handleTreeItemFolderClick={handleTreeItemFolderClick}
                      setFoldersMeta={setFoldersMeta}
                      setRecentDeletedFolder={setRecentDeletedFolder}
                      refetchAllFolder={refetchAllFolder}
                    />
                  ) : null}

                  <Badge variant='default' className='font-thin'>
                    {renderTotalFiles()}
                  </Badge>
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        variant='outline'
                        size='icon'
                        onClick={() => setView((pre) => (pre == 0 ? 1 : 0))}
                      >
                        {view == 0 ? <List size={16} /> : <Grid size={16} />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {translate(view == 0 ? "List View" : "Grid View")}
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
            <RenderFilter />
            {view ? (
              <div
                className='mt-2 overflow-y-scroll'
                style={{
                  height: `${showAsModal && window?.innerWidth > 1024 ? "calc(100% - 5rem)" : window?.innerWidth < 425 ? "calc(100% - 15rem)" : "calc(100% - 10rem)"}`,
                }}
              >
                {isFetching || isLoading ? (
                  <PmsLoader className='min-h-[7vh]' />
                ) : (
                  activeFolder?.id > 0 &&
                  folderItems[activeFolder?.id] &&
                  folderItems[activeFolder?.id].length > 0 &&
                  folderItems[activeFolder?.id].map((item: DictType, index: number) => {
                    return (
                      <div
                        key={item.id}
                        className='group relative m-1 flex cursor-pointer items-center justify-between space-x-4 rounded-2xl border border-gray-200 bg-white p-4 shadow dark:border-gray-700 dark:bg-gray-800'
                        onClick={() => {
                          if (item.as === "folder") {
                            if (filterActive == false) handleTreeItemFolderClick(item, true)
                            else {
                              setClearFilter(true)
                              setNewActiveData(item)
                            }
                          }
                        }}
                      >
                        <div className='flex items-center space-x-4'>
                          {item.as === "folder" ? (
                            <RenderFileIcon styleClass='h-10 w-10' type={handleFileType(item)} />
                          ) : (
                            <FilePreview
                              file={item}
                              isSelectionOn={showBatchSelection}
                              onClick={() => handleClick(item)}
                            >
                              {imageExtensions.includes(item.type) ? (
                                <img
                                  src={item?.file}
                                  alt='image'
                                  className='h-10 w-10 rounded object-cover'
                                  loading='lazy'
                                />
                              ) : (
                                <RenderFileIcon
                                  styleClass='h-10 w-10'
                                  type={handleFileType(item)}
                                />
                              )}
                            </FilePreview>
                          )}

                          <div className='max-w-[calc(100vw-12rem)] truncate'>
                            <p
                              className={cn(
                                "truncate text-sm font-medium text-gray-900 dark:text-white",
                                item?.deleted ? "text-red-500" : "",
                              )}
                            >
                              {item.name}
                            </p>
                            {item.as === "folder" ? (
                              <p className='text-xs capitalize text-gray-500'>
                                {translate("files")}:{" "}
                                {item._files_in_folder > 99
                                  ? `+${item._files_in_folder}`
                                  : item._files_in_folder}
                              </p>
                            ) : null}

                            <p className='text-xs capitalize text-gray-500'>{translate(item.as)}</p>
                          </div>
                        </div>

                        {item.as != "folder" ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant='ghost'
                                size='icon'
                                className='h-6 w-6 p-0 hover:bg-white'
                                onClick={(e) => e.stopPropagation()}
                              >
                                <EllipsisVertical className='h-4 w-4 text-gray-500' />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className='w-fit p-2'>
                              <div className='flex flex-col gap-2'>
                                <button
                                  className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    downloadFile(item)
                                  }}
                                >
                                  <div className='px-2'>
                                    <Download size={16} />
                                  </div>
                                  <span className='text-sm'>{translate("Download File")}</span>
                                </button>
                                <button
                                  className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setIsMenuView(true)
                                    setSelectedMoveFile(item)
                                    setMoveFileModal(!moveFileModal)
                                  }}
                                >
                                  <div className='flex w-full items-center gap-2'>
                                    <div className='px-2'>
                                      <FolderSearch className='' size={16} />
                                    </div>
                                    <span className='text-sm'>{`${translate("Move file")}`}</span>
                                  </div>
                                </button>
                                <button
                                  className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <CutFile
                                    file={item}
                                    folderItems={folderItems}
                                    activeFolder={activeFolder}
                                    setCutFiles={setCutFiles}
                                    setPreFolderItems={setPreFolderItems}
                                    setFolderId={setFolderId}
                                    setFolderItems={setFolderItems}
                                    isMenuView={true}
                                  />
                                </button>
                                <button
                                  className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <CopyFile
                                    file={item}
                                    setCopyFiles={setCopyFiles}
                                    isMenuView={true}
                                  />
                                </button>
                                <button
                                  className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ShareFileOnWhatsApp file={item} isMenuView={true} />
                                </button>
                                <button
                                  className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setSelectedFiles([item])
                                    setShareViaEmailModal(true)
                                  }}
                                >
                                  <div className='px-2'>
                                    <Mail size={14} />
                                  </div>
                                  <span className='text-sm'>{`${translate("Share on mail")}`}</span>
                                </button>
                                {printAble.includes(item?.type) ? (
                                  <button
                                    className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <PrintFile file={item} isMenuView={true} />
                                  </button>
                                ) : null}
                                <button
                                  className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <DeleteFile
                                    file={item}
                                    isMenuView={true}
                                    setFolderItems={setFolderItems}
                                    setBreadcrumbs={setBreadcrumbs}
                                    foldersMeta={foldersMeta}
                                    setRecentDeletedFile={setRecentDeletedFile}
                                    refetchFolderContent={refetchFolderContent}
                                  />
                                </button>
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : null}
                      </div>
                    )
                  })
                )}
              </div>
            ) : (
              <div
                id='file-main-div'
                ref={observerRef}
                className='overflow-y-scroll'
                style={{
                  height: `${showAsModal && window?.innerWidth > 1024 ? "calc(100% - 6rem)" : window?.innerWidth < 425 ? "calc(100% - 15rem)" : "calc(100% - 10rem)"}`,
                }}
              >
                <InfiniteScroll
                  dataLength={folderItems[activeFolder?.id]?.length ?? 0}
                  next={handleLoadMoreData}
                  hasMore={!!nextPage}
                  loader={isFetching || isLoading ? <PmsLoader className='min-h-[7vh]' /> : null}
                  scrollableTarget='file-main-div'
                  endMessage={
                    folderItems[activeFolder?.id]?.length > 0 &&
                    pageParam !== null &&
                    pageParam > 1 &&
                    !nextPage && (
                      <div className='file_loader'>
                        <p>{translate("No more files")}</p>
                      </div>
                    )
                  }
                >
                  <div className='grid grid-cols-2 gap-6 p-4 md:grid-cols-3 lg:grid-cols-4'>
                    {(isLoading || isFetching) && Object.values(folderItems).length === 0 && (
                      <div className='col-span-4 h-full w-full'>
                        <PmsLoader />
                      </div>
                    )}

                    {activeFolder?.id > 0 &&
                      folderItems[activeFolder?.id] &&
                      folderItems[activeFolder?.id].length > 0 &&
                      folderItems[activeFolder?.id].map((item: DictType, index: number) => (
                        <DraggableItem item={item} key={`drag_${item.id}_${index}`}>
                          {item?.file ? (
                            <div
                              className={`col-span-1 flex w-full flex-col justify-center overflow-hidden ${batchData.length > 0 && batchData.find((val) => item.id === val.id) && "bg-accent"}`}
                            >
                              <HoverCard>
                                <>
                                  {showBatchSelection && (
                                    <input
                                      name='check'
                                      ref={checkboxRef as any}
                                      type='checkbox'
                                      id={`check-${item.id}`}
                                      className='relative h-[17px] w-[17px] self-end'
                                      checked={
                                        batchData.length > 0 &&
                                          batchData?.find((val) => val.id === item.id)
                                          ? true
                                          : false
                                      }
                                      onChange={(event: SyntheticEvent) =>
                                        handleSelection(item, event)
                                      }
                                      alt='select'
                                    />
                                  )}
                                  <FilePreview
                                    file={item}
                                    isSelectionOn={showBatchSelection}
                                    onClick={() => handleClick(item)}
                                  >
                                    <HoverCardTrigger className='flex flex-col items-center justify-center'>
                                      {imageExtensions.includes(item.type) ? (
                                        <img
                                          src={item?.file}
                                          alt='image'
                                          className='h-20 w-20 rounded object-cover'
                                          loading='lazy'
                                        />
                                      ) : (
                                        <RenderFileIcon size='lg' type={handleFileType(item)} />
                                      )}
                                      <span
                                        className={cn(
                                          "w-[80%] truncate text-center text-xs",
                                          item.deleted ? "text-red-500" : "",
                                        )}
                                      >
                                        {item?.name}
                                      </span>
                                      {handleShowTask(item)}
                                    </HoverCardTrigger>
                                  </FilePreview>
                                </>
                                <HoverCardContent className='flex w-fit items-center gap-2'>
                                  <DownloadFile file={item} />
                                  {item.deleted &&
                                    (isOrganizationOwner ||
                                      isAdmin ||
                                      item.deleted_by == authConfig.id) ? (
                                    <RestoreFile file={item} />
                                  ) : null}
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <Button
                                        size={"icon"}
                                        className='h-6 w-7'
                                        variant={"ghost"}
                                        onClick={() => {
                                          setIsMenuView(false)
                                          setSelectedMoveFile(item)
                                          setMoveFileModal(!moveFileModal)
                                        }}
                                      >
                                        <FolderSearch className='' size={16} />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent className=''>
                                      {translate("Move file")}
                                    </TooltipContent>
                                  </Tooltip>
                                  <CutFile
                                    file={item}
                                    folderItems={folderItems}
                                    activeFolder={activeFolder}
                                    setCutFiles={setCutFiles}
                                    setPreFolderItems={setPreFolderItems}
                                    setFolderItems={setFolderItems}
                                    setFolderId={setFolderId}
                                  />
                                  <CopyFile file={item} setCopyFiles={setCopyFiles} />
                                  <ShareFileOnWhatsApp file={item} />
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size={"icon"}
                                        className='h-6 w-7'
                                        variant={"ghost"}
                                        aria-label={translate("Share on Mail")}
                                        onClick={() => {
                                          setSelectedFiles([item])
                                          setShareViaEmailModal(true)
                                        }}
                                      >
                                        {/* Mail */}
                                        <Mail size={14} />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>{translate("Share on Mail")}</TooltipContent>
                                  </Tooltip>
                                  <PrintFile file={item} />
                                  <DeleteFile
                                    file={item}
                                    setFolderItems={setFolderItems}
                                    setBreadcrumbs={setBreadcrumbs}
                                    foldersMeta={foldersMeta}
                                    setRecentDeletedFile={setRecentDeletedFile}
                                    refetchFolderContent={refetchFolderContent}
                                  />
                                </HoverCardContent>
                              </HoverCard>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                if (filterActive == false) handleTreeItemFolderClick(item, true)
                                else {
                                  setClearFilter(true)
                                  setNewActiveData(item)
                                }
                              }}
                              className='col-span-1 flex w-full flex-col items-center justify-center'
                            >
                              <div className='relative'>
                                <div
                                  className={`absolute right-0 top-[5px] ${item._files_in_folder > 99 ? "h-9 w-10" : "h-8 w-8"} align-center flex justify-center rounded-full bg-blue-100 p-1`}
                                >
                                  <span className='p-0 text-center'>
                                    {item._files_in_folder > 99
                                      ? `+${item._files_in_folder}`
                                      : item._files_in_folder}
                                  </span>
                                </div>
                                <RenderFileIcon size='lg' type={handleFileType(item)} />
                              </div>
                              <span
                                className={cn(
                                  "w-[80%] truncate text-center text-xs",
                                  item.deleted ? "text-red-500" : "",
                                )}
                              >
                                {item?.name}
                              </span>
                            </button>
                          )}
                        </DraggableItem>
                      ))}
                    {isFetched &&
                      (activeFolder?.id <= 0 ||
                        !folderItems[activeFolder?.id] ||
                        folderItems[activeFolder?.id].length <= 0) && (
                        <div className='col-span-4 flex h-36 w-full flex-col items-center justify-center gap-2'>
                          <span className='text-lg font-semibold text-muted-foreground'>
                            {translate("Folder is Empty")}
                          </span>{" "}
                          <UploadFile
                            projectId={project?.id}
                            revalidate={refetch}
                            folder={activeFolder}
                            mainView={true}
                          />
                        </div>
                      )}
                  </div>
                </InfiniteScroll>
              </div>
            )}
          </div>
          {createPortal(
            <DragOverlay>
              {activeFileInfinite && (
                <div className='flex flex-col items-center justify-center rounded-lg bg-gray-100 p-4 opacity-80 shadow-xl'>
                  <RenderFileIcon size='lg' type={handleFileType(activeFileInfinite)} />
                  <span className='w-[80%] truncate text-center text-sm font-medium'>
                    {activeFileInfinite?.name}
                  </span>
                </div>
              )}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>
      </Card>

      {moveFileModal && (
        <MoveFile
          file={selectedMoveFile}
          moveFileModal={moveFileModal}
          resetPageParam={resetPageParam}
          setMoveFileModal={setMoveFileModal}
          rootFolder={rootFolder}
          parentFolder={activeFolder}
          setCutFiles={setCutFiles}
          isMenuView={isMenuView}
          setFolderItems={setFolderItems}
          foldersMeta={foldersMeta}
          folderItems={folderItems}
          cutFiles={cutFiles}
          setFoldersMeta={setFoldersMeta}
          handleBatchSelection={handleBatchSelection}
          handleTreeItemFolderClick={handleTreeItemFolderClick}
          fileArr={[]}
          setBreadcrumbs={setBreadcrumbs}
          breadcrumbs={breadcrumbs}
          setActiveFolder={setActiveFolder}
        />
      )}

      {showBatchSelection && (
        <FilesBatchActions
          tasks={batchData}
          showBatchSelection={showBatchSelection}
          handleBatchSelection={handleBatchSelection}
          rootFolder={rootFolder}
          resetPageParam={resetPageParam}
          activeFolder={activeFolder}
          selectAllFiles={selectAllFiles}
          totalFiles={folderItems[activeFolder?.id].length}
          setCutFiles={setCutFiles}
          setCopyFiles={setCopyFiles}
          clearSelection={clearSelection}
          setFolderItems={setFolderItems}
          setBreadcrumbs={setBreadcrumbs}
          foldersMeta={foldersMeta}
          folderItems={folderItems}
          cutFiles={cutFiles}
          handleTreeItemFolderClick={handleTreeItemFolderClick}
          setFoldersMeta={setFoldersMeta}
          breadcrumbs={breadcrumbs}
          setActiveFolder={setActiveFolder}
          setPreFolderItems={setPreFolderItems}
          setFolderId={setFolderId}
          setShareViaEmailModal={setShareViaEmailModal}
          setSelectedFiles={setSelectedFiles}
        />
      )}

      {shareViaEmailModal && (
        <ShareFilesFoldersViaEmail
          modalOpen={shareViaEmailModal}
          setModalOpen={setShareViaEmailModal}
          selectedFiles={selectedFiles}
          project={project}
        />
      )}
    </Fragment>
  )
}

interface FileIconProps {
  type: FileType
  size?: "lg" | "sm"
  styleClass?: string
}

export const RenderFileIcon = ({ type, size, styleClass }: FileIconProps) => {
  const fileType = type?.toLocaleLowerCase()

  const imageExtensions = [
    "jpg",
    "jpeg",
    "jpe",
    "jif",
    "jfif",
    "jfi",
    "png",
    "webp",
    "gif",
    "bmp",
    "dib",
    "tif",
    "tiff",
    "heif",
    "heic",
    "ico",
    "cur",
    "svg",
    "svgz",
    "avif",
    "apng",
    "raw",
    "arw",
    "cr2",
    "nrw",
    "k25",
    "log",
  ]
  const videoExtensions = ["mp4", "mov", "webm"]
  const audioExtensions = ["mp3"]
  const fileExtensions = ["dwg", "ifc", "txt"]
  const documentExtensions = ["doc", "docx"]
  const sheetExtensions = ["xlsx", "xls", "csv"]
  const presentationExtensions = ["pptx", "ppt"]
  const jsonExtension = ["json"]
  const pdfExtension = ["pdf"]
  const zipExtensions = ["zip", "rar", "7z"]

  if (imageExtensions.includes(fileType)) {
    return (
      <FileImageIcon
        className={cn("h-5 w-5 text-green-500", size === "lg" && "h-20 w-20", styleClass)}
      />
    )
  } else if (videoExtensions.includes(fileType)) {
    return (
      <FileVideo className={cn("h-5 w-5 text-red-500", size === "lg" && "h-20 w-20", styleClass)} />
    )
  } else if (audioExtensions.includes(fileType)) {
    return (
      <FileAudio2
        className={cn("h-5 w-5 text-purple-500", size === "lg" && "h-20 w-20", styleClass)}
      />
    )
  } else if (documentExtensions.includes(fileType)) {
    return (
      <FileText className={cn("h-5 w-5 text-primary", size === "lg" && "h-20 w-20", styleClass)} />
    )
  } else if (sheetExtensions.includes(fileType)) {
    return (
      <FileSpreadsheet
        className={cn("h-5 w-5 text-green-700", size === "lg" && "h-20 w-20", styleClass)}
      />
    )
  } else if (presentationExtensions.includes(fileType)) {
    return (
      <FilePieChart
        className={cn("h-5 w-5 text-orange-500", size === "lg" && "h-20 w-20", styleClass)}
      />
    )
  } else if (fileExtensions.includes(fileType)) {
    return (
      <File className={cn("h-5 w-5 text-slate-500", size === "lg" && "h-20 w-20", styleClass)} />
    )
  } else if (jsonExtension.includes(fileType)) {
    return (
      <FileJson
        className={cn("h-5 w-5 text-yellow-500", size === "lg" && "h-20 w-20", styleClass)}
      />
    )
  } else if (pdfExtension.includes(fileType)) {
    return (
      <img
        src={`${process.env.PUBLIC_URL}/pdf.svg`}
        className={cn("h-5 w-5", size === "lg" && "h-20 w-20", styleClass)}
      />
    )
  } else if (zipExtensions.includes(fileType)) {
    return (
      <FolderArchive
        className={cn("h-5 w-5 text-gray-500", size === "lg" && "h-20 w-20", styleClass)}
      />
    )
  } else {
    return (
      <FolderIcon
        className={cn("h-5 w-5 text-gray-500", size === "lg" && "h-20 w-20", styleClass)}
      />
    )
  }
}
